import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CityVerdict } from "@/components/CityVerdict";
import { JusticeMap } from "@/components/JusticeMap";
import { Eye, Loader2, Unlock, Fingerprint, Scale, Globe2 } from "lucide-react";

const GRADE = { A: "#00FF7F", B: "#84cc16", C: "#F59E0B", D: "#F97316", E: "#EF4444", "—": "#475569" };
const P = [["safety", "#F59E0B"], ["environment", "#00FF7F"], ["rent_fairness", "#00FFFF"], ["transparency", "#8A2BE2"]];
const ICONS = { no_gatekeepers: Unlock, radical_transparency: Fingerprint, comparative_fairness: Scale, data_commons: Globe2 };

export default function Justice() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { axios.get(`${API}/renewal/map`).then((r) => setRenewals(r.data)).catch(() => {}); }, []);

  const load = () => axios.get(`${API}/justice/index`).then((r) => {
    setData(r.data);
    setSel((cur) => r.data.cities.find((c) => c.city === cur?.city) || r.data.cities[0]);
  }).catch(() => setData({ cities: [], weights: {}, principles: [], summary: {} }));
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#050A15]">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <span className="inline-flex items-center gap-2 text-[#8A2BE2] text-xs font-mono uppercase tracking-widest"><Eye className="h-4 w-4" /> God's Eye · {t("jf_tag")}</span>
        <h1 className="font-display font-bold text-3xl md:text-5xl mt-2 tracking-tight" data-testid="justice-title">Utopian-Revolutionary Justice Index</h1>
        <p className="text-slate-400 mt-3 max-w-3xl leading-relaxed">{t("jf_sub")}</p>

        {data && (
          <div className="flex flex-wrap gap-2 mt-5" data-testid="principles">
            {data.principles?.map((p) => { const I = ICONS[p.key]; return (
              <span key={p.key} className="inline-flex items-center gap-1.5 border border-white/10 px-3 py-1.5 text-xs text-slate-300"><I className="h-3.5 w-3.5 text-[#8A2BE2]" /> {p[lang] || p.en}</span>
            ); })}
            <span className="inline-flex items-center gap-2 text-xs font-mono text-slate-500 ml-auto" data-testid="justice-summary">
              {data.summary.scored_cities} cities scored · mean {data.summary.mean_index ?? "—"} · {data.summary.total_claims} claims
            </span>
          </div>
        )}

        {data && data.cities.length > 0 && (
          <div className="mt-8">
            <JusticeMap cities={data.cities} selected={sel} onSelect={setSel} renewals={renewals} onSelectRenewal={(r) => navigate(`/renewal?case=${r.id}`)} />
            {renewals.length > 0 && <p className="text-[10px] font-mono text-slate-500 mt-1" data-testid="map-renewal-note">◆ {renewals.length} renewal cases · {renewals.filter((r) => r.status === "completed").length} completed · click a diamond to open the case</p>}
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-6 mt-6">
          <div className="lg:col-span-3 qs-panel overflow-hidden" data-testid="justice-table">
            <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/10">
              <span className="col-span-1">#</span><span className="col-span-3">City</span><span className="col-span-2 text-right">Index</span>
              <span className="col-span-5 pl-3">Pillars · safety / environment / rent / civic</span><span className="col-span-1 text-right">Claims</span>
            </div>
            {!data ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#8A2BE2]" /></div>
              : data.cities.map((c) => (
                <button key={c.city} data-testid={`city-row-${c.city}`} onClick={() => setSel(c)}
                  className={`w-full grid grid-cols-12 gap-2 items-center px-4 py-3 text-left border-b border-white/5 transition-colors hover:bg-white/[0.03] ${sel?.city === c.city ? "bg-[#8A2BE2]/10" : ""}`}>
                  <span className="col-span-1 font-mono text-xs text-slate-500">{c.rank ?? "—"}</span>
                  <span className="col-span-3 font-display font-semibold truncate">{c.city}</span>
                  <span className="col-span-2 text-right font-mono font-bold" style={{ color: GRADE[c.grade] }}>{c.index ?? "—"} <span className="text-[10px] text-slate-500">{c.grade}</span></span>
                  <span className="col-span-5 flex gap-1 pl-3">
                    {P.map(([k, col]) => (
                      <span key={k} className="flex-1 h-2 bg-[#050A15] relative overflow-hidden" title={`${k}: ${c.pillars[k] ?? "no data"}`}>
                        <span className="absolute inset-y-0 left-0" style={{ width: `${c.pillars[k] ?? 0}%`, background: c.pillars[k] == null ? "transparent" : col }} />
                      </span>
                    ))}
                  </span>
                  <span className="col-span-1 text-right font-mono text-xs text-slate-400">{c.evidence.attestations + c.evidence.disputes}</span>
                </button>
              ))}
            {data && (
              <p className="text-[11px] text-slate-600 px-4 py-3">
                Weights: {Object.entries(data.weights).map(([k, v]) => `${k} ${v}`).join(" · ")}. Cities with no evidence are unranked — contribute a scan to put them on the map.
              </p>
            )}
          </div>
          <div className="lg:col-span-2">
            {sel && <CityVerdict city={sel} weights={data.weights} onClaimFiled={load} />}
          </div>
        </div>
        <p className="text-[11px] font-mono text-slate-600 mt-6">{t("jf_note")}</p>
      </div>
      <Footer />
    </div>
  );
}
